/**
 * The thumb-zone primitive (TASKS T44). **[DoD-17]**
 *
 * > Every interactive control in the lower 60% at 390×844, enforced **structurally** rather than
 * > per-card.
 *
 * Structurally means two things, and the distinction is the whole point of this module.
 *
 *  1. **Placement is not a card's decision.** `Shell` mounts `CardSpec.controls` into
 *     `.card__controls` and nothing else, and CSS pins that region below the thumb line. A card
 *     cannot put a control anywhere else, because a card never touches the DOM tree.
 *  2. **The guard checks the thing the rule is about, not a proxy.** Seventeen cards each
 *     remembering a layout rule is how the guarantee rots one card at a time — invisibly, until
 *     one specific card is on screen at one specific viewport. So this runs on every mount and
 *     fails the build the first time it is violated.
 *
 * Both checks are here because they fail differently. The structural one catches a control in the
 * read-only region and needs no layout, so it fires even on a card that was never painted. The
 * geometric one catches a CSS regression that moved the region itself, which no amount of correct
 * card code would prevent.
 */

/** Controls must begin at or below this fraction of the viewport height (BRIEF: lower 60%). */
export const THUMB_ZONE_FRACTION = 0.4;

/**
 * What counts as interactive.
 *
 * Broader than "things with a click handler" on purpose: DoD-17 is about what a **thumb has to
 * reach**, so anything focusable or tappable qualifies, including a `tabindex`-ed div a future card
 * might reach for. `tabindex="-1"` is excluded — programmatically focusable is not reachable by
 * thumb or by Tab.
 */
export const INTERACTIVE_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable]:not([contenteditable="false"])',
].join(', ');

export type ViolationKind = 'outside-controls' | 'above-thumb-line';

export interface ThumbZoneViolation {
  kind: ViolationKind;
  /** `data-testid` where there is one, else enough of the element to find it. */
  control: string;
  message: string;
}

/** Identify a node in a failure message well enough to go and fix it. */
export function describeControl(node: Element): string {
  const testId = node instanceof HTMLElement ? node.dataset['testid'] : undefined;
  if (testId) return testId;
  const label = node.textContent?.trim().slice(0, 24);
  return label ? `${node.tagName.toLowerCase()} "${label}"` : node.tagName.toLowerCase();
}

/**
 * Interactive nodes that are not inside the control region.
 *
 * Needs no layout, so it is the check that still works on a hidden card, in a unit test, or before
 * the first paint.
 */
export function findMisplacedControls(card: ParentNode): ThumbZoneViolation[] {
  const out: ThumbZoneViolation[] = [];
  for (const node of card.querySelectorAll(INTERACTIVE_SELECTOR)) {
    if (node.closest('.card__controls')) continue;
    out.push({
      kind: 'outside-controls',
      control: describeControl(node),
      message:
        'is interactive but sits outside .card__controls — put it in the card\'s `controls`, ' +
        'never in `display`',
    });
  }
  return out;
}

/**
 * Interactive nodes whose top edge is above the thumb line.
 *
 * Zero-sized nodes are skipped: an element that is not rendered cannot be out of reach, and a
 * display-none control would otherwise report a phantom violation at y=0.
 */
export function findControlsAboveLine(
  card: ParentNode,
  viewportHeight: number,
): ThumbZoneViolation[] {
  const threshold = viewportHeight * THUMB_ZONE_FRACTION;
  const out: ThumbZoneViolation[] = [];
  for (const node of card.querySelectorAll(INTERACTIVE_SELECTOR)) {
    const box = node.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) continue;
    if (box.top >= threshold) continue;
    out.push({
      kind: 'above-thumb-line',
      control: describeControl(node),
      message: `starts at y=${box.top.toFixed(1)}, above the thumb line at y=${threshold.toFixed(1)}`,
    });
  }
  return out;
}

export function findViolations(card: ParentNode, viewportHeight: number): ThumbZoneViolation[] {
  return [...findMisplacedControls(card), ...findControlsAboveLine(card, viewportHeight)];
}

export function formatViolations(violations: readonly ThumbZoneViolation[]): string {
  return violations.map((v) => `  ${v.control} ${v.message}`).join('\n');
}

export class ThumbZoneError extends Error {
  readonly violations: readonly ThumbZoneViolation[];

  constructor(cardName: string, violations: readonly ThumbZoneViolation[]) {
    super(
      `thumb zone violated by card "${cardName}" (DoD-17):\n${formatViolations(violations)}\n` +
        'Every interactive control must sit in the lower 60% of the viewport.',
    );
    this.name = 'ThumbZoneError';
    this.violations = violations;
  }
}

export interface GuardOptions {
  /**
   * Throw on violation instead of reporting it.
   *
   * True in development and under the test harness, false in a production build. A misplaced
   * control is a programming error and should stop the build — but it must not white-screen the
   * game at 3:40 a.m. over a layout nit, when a control that is merely hard to reach is strictly
   * better than no game at all. The tests are where this is meant to be caught, and they run with
   * it on. See DECISIONS D-089.
   */
  strict: boolean;
  doc?: Document;
  /** Defaults to the layout viewport. Injectable so a test can assert against 844 exactly. */
  viewportHeight?: number;
  onViolation?: (violations: readonly ThumbZoneViolation[], cardName: string) => void;
}

/**
 * Build the `Shell.afterRender` hook that enforces the zone on every mount.
 *
 * Runs on the top layer only. Layers beneath it are `inert` — unreachable by thumb, pointer, or
 * Tab — so their controls are not controls for the purposes of this rule.
 */
export function thumbZoneGuard(
  options: GuardOptions,
): (card: HTMLElement, spec: { name: string }) => void {
  const doc = options.doc ?? document;
  return (card, spec) => {
    const viewportHeight = options.viewportHeight ?? doc.documentElement.clientHeight;
    // A zero-height viewport means the page is not laid out yet; every control would "violate".
    if (viewportHeight <= 0) return;

    const violations = findViolations(card, viewportHeight);
    if (violations.length === 0) return;

    options.onViolation?.(violations, spec.name);
    if (options.strict) throw new ThumbZoneError(spec.name, violations);
    console.error(new ThumbZoneError(spec.name, violations).message);
  };
}
